import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchFormDefinition } from '../../../../../services/formDefinitionsApi';
import type { FormFlowDefinition, FormFlowStep } from '../../../../../types/formFlow';
import {
  fetchWorkerRegistryProgress,
  upsertWorkerRegistryProgress,
  WORKER_REGISTRY_DONE_STEP_ID,
} from '../../../../../services/workerRegistryProgressApi';
import { formatCommittedDisplay } from './workerRegistryDisplay';
import { buildCommittedFromRestore } from './restoreWorkerRegistryState';
import type { WorkerRegistryCommittedEntry } from './workerRegistryTypes';
import {
  workerRegistryActiveSteps,
  workerRegistryClearPreviousJobAnswers,
  workerRegistryCommittedWithoutPreviousJob,
  workerRegistryHasWorkExperience,
  workerRegistryStepByFieldName,
  WORKER_REGISTRY_PREVIOUS_JOB_FIELD_NAMES,
} from './workerRegistrySteps';

export type { WorkerRegistryCommittedEntry } from './workerRegistryTypes';

function cursorStepIdForSave(steps: FormFlowStep[], stepIndex: number): string | null {
  if (stepIndex >= steps.length) {
    return WORKER_REGISTRY_DONE_STEP_ID;
  }
  return steps[stepIndex]?.stepId ?? null;
}

/** Pause after the user bubble appears before showing the next assistant question (ms). */
const NEXT_ASSISTANT_QUESTION_DELAY_MS = 520;

export function useWorkerRegistryChat(
  formCode: string,
  t: (key: string) => string,
  wheelLocale: string,
  currentEmail: string | null | undefined
) {
  const [definition, setDefinition] = useState<FormFlowDefinition | null>(null);
  const [defLoadError, setDefLoadError] = useState(false);
  const [defLoading, setDefLoading] = useState(true);
  const [progressReady, setProgressReady] = useState(() => !currentEmail?.trim());

  /** After server restore: skip timed thread intro (same as checklist resume). */
  const [skipIntroDelay, setSkipIntroDelay] = useState(false);

  /** Index into {@link activeSteps} (equals committed.length in normal flow). */
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [committed, setCommitted] = useState<WorkerRegistryCommittedEntry[]>([]);
  /** When set, user is editing one committed answer from the completed form (no replay of later steps). */
  const [editingCommitIndex, setEditingCommitIndex] = useState<number | null>(null);

  /** Hide the “current” assistant question briefly right after submit so the user bubble reads first. */
  const [suppressNextAssistantBubble, setSuppressNextAssistantBubble] = useState(false);
  const assistantHoldTimerRef = useRef<number | null>(null);

  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const clearAssistantHold = useCallback(() => {
    if (assistantHoldTimerRef.current != null) {
      window.clearTimeout(assistantHoldTimerRef.current);
      assistantHoldTimerRef.current = null;
    }
    setSuppressNextAssistantBubble(false);
  }, []);

  useEffect(() => {
    return () => clearAssistantHold();
  }, [clearAssistantHold]);

  useEffect(() => {
    let cancelled = false;
    setDefLoading(true);
    setDefLoadError(false);
    fetchFormDefinition(formCode)
      .then((d) => {
        if (cancelled) return;
        if (!d || !d.steps?.length) {
          setDefinition(null);
          setDefLoadError(true);
        } else {
          setDefinition(d);
          setDefLoadError(false);
        }
        setDefLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setDefinition(null);
        setDefLoadError(true);
        setDefLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [formCode]);

  useEffect(() => {
    setProgressReady(!currentEmail?.trim());
    setSkipIntroDelay(false);
    setEditingCommitIndex(null);
  }, [currentEmail, formCode]);

  useEffect(() => {
    const email = currentEmail?.trim();
    if (!email || !definition?.steps?.length) {
      return;
    }
    let cancelled = false;
    setProgressReady(false);
    fetchWorkerRegistryProgress(email, formCode)
      .then((data) => {
        if (cancelled || !data) return;
        const vals = data.values ?? {};
        setAnswers(vals);
        const built = buildCommittedFromRestore(definition.steps, vals, data.currentStepId, t, wheelLocale);
        setCommitted(built.committed);
        setStepIndex(built.stepIndex);
        setSkipIntroDelay(built.committed.length > 0 || built.isComplete);
      })
      .finally(() => {
        if (!cancelled) setProgressReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [definition, formCode, currentEmail, t, wheelLocale]);

  const persistSnapshot = useCallback(
    (
      nextAnswers: Record<string, string>,
      nextStepIndex: number,
      stepsSnapshot: FormFlowStep[]
    ) => {
      const email = currentEmail?.trim();
      if (!email) return;
      const currentStepId = cursorStepIdForSave(stepsSnapshot, nextStepIndex);
      void upsertWorkerRegistryProgress({
        email,
        formCode,
        values: nextAnswers,
        currentStepId,
      });
    },
    [currentEmail, formCode]
  );

  const allSteps = definition?.steps ?? [];
  const activeSteps = useMemo(
    () => workerRegistryActiveSteps(allSteps, committed),
    [allSteps, committed]
  );

  const currentStep: FormFlowStep | undefined = useMemo(() => {
    if (allSteps.length === 0) return undefined;
    if (editingCommitIndex !== null) {
      const entry = committed[editingCommitIndex];
      if (!entry) return undefined;
      return workerRegistryStepByFieldName(allSteps, entry.fieldName);
    }
    if (stepIndex < activeSteps.length) return activeSteps[stepIndex];
    return undefined;
  }, [allSteps, activeSteps, stepIndex, editingCommitIndex, committed]);

  const isComplete =
    activeSteps.length > 0 && stepIndex >= activeSteps.length && editingCommitIndex === null;

  const loading = defLoading || (Boolean(currentEmail?.trim()) && !progressReady);
  const loadError = defLoadError;

  const setField = useCallback((fieldName: string, value: string) => {
    setAnswers((a) => ({ ...a, [fieldName]: value }));
  }, []);

  const currentValue = currentStep ? (answers[currentStep.fieldName] ?? '') : '';

  const canSubmitCurrent = useMemo(() => {
    if (!currentStep) return false;
    const v = (answers[currentStep.fieldName] ?? '').trim();
    if (!currentStep.required) return true;
    if (currentStep.fieldType === 'date') return v.length > 0;
    if (currentStep.fieldType === 'choice') return v.length > 0;
    return v.length > 0;
  }, [answers, currentStep]);

  const advance = useCallback(() => {
    if (!currentStep) return;
    const raw = (answers[currentStep.fieldName] ?? '').trim();
    if (currentStep.required && !raw) return;

    const displayText = formatCommittedDisplay(currentStep, raw, t, wheelLocale);
    const fullStepIndex = allSteps.findIndex((s) => s.stepId === currentStep.stepId);
    const entry: WorkerRegistryCommittedEntry = {
      stepIndex: editingCommitIndex !== null ? committed[editingCommitIndex]?.stepIndex ?? fullStepIndex : fullStepIndex,
      stepId: currentStep.stepId,
      fieldName: currentStep.fieldName,
      displayText: displayText || raw,
      rawValue: raw,
    };

    if (editingCommitIndex !== null) {
      const idx = editingCommitIndex;
      let nextAnswers = { ...answers, [currentStep.fieldName]: raw };

      if (currentStep.fieldName === 'constructionExperienceYears') {
        const hadExperience = workerRegistryHasWorkExperience(committed[idx]?.rawValue ?? '');
        const hasExperience = workerRegistryHasWorkExperience(raw);

        if (!hasExperience) {
          nextAnswers = workerRegistryClearPreviousJobAnswers(nextAnswers);
          const trimmedCommitted = workerRegistryCommittedWithoutPreviousJob([
            ...committed.slice(0, idx),
            entry,
          ]);
          setAnswers(nextAnswers);
          setCommitted(trimmedCommitted);
          setEditingCommitIndex(null);
          const stepsAfter = workerRegistryActiveSteps(allSteps, trimmedCommitted);
          setStepIndex(stepsAfter.length);
          persistSnapshot(nextAnswers, stepsAfter.length, stepsAfter);
          return;
        }

        setAnswers(nextAnswers);
        let nextCommitted = [...committed];
        nextCommitted[idx] = entry;
        setCommitted(nextCommitted);
        setEditingCommitIndex(null);

        if (!hadExperience && hasExperience) {
          const stepsAfter = workerRegistryActiveSteps(allSteps, nextCommitted);
          const resumeIndex = idx + 1;
          setStepIndex(resumeIndex);
          persistSnapshot(nextAnswers, resumeIndex, stepsAfter);
          return;
        }

        const stepsAfter = workerRegistryActiveSteps(allSteps, nextCommitted);
        setStepIndex(stepsAfter.length);
        persistSnapshot(nextAnswers, stepsAfter.length, stepsAfter);
        return;
      }

      const nextCommitted = [...committed];
      nextCommitted[idx] = entry;
      setAnswers(nextAnswers);
      setCommitted(nextCommitted);
      setEditingCommitIndex(null);
      const stepsAfter = workerRegistryActiveSteps(allSteps, nextCommitted);
      setStepIndex(stepsAfter.length);
      persistSnapshot(nextAnswers, stepsAfter.length, stepsAfter);
      return;
    }

    if (assistantHoldTimerRef.current != null) {
      window.clearTimeout(assistantHoldTimerRef.current);
      assistantHoldTimerRef.current = null;
    }
    setSuppressNextAssistantBubble(true);
    assistantHoldTimerRef.current = window.setTimeout(() => {
      assistantHoldTimerRef.current = null;
      setSuppressNextAssistantBubble(false);
    }, NEXT_ASSISTANT_QUESTION_DELAY_MS);

    let nextAnswers = { ...answers, [currentStep.fieldName]: raw };
    if (
      currentStep.fieldName === 'constructionExperienceYears' &&
      !workerRegistryHasWorkExperience(raw)
    ) {
      nextAnswers = workerRegistryClearPreviousJobAnswers(nextAnswers);
    }
    setAnswers(nextAnswers);

    setCommitted((prev) => {
      const nextCommitted = [...prev, entry];
      const stepsAfter = workerRegistryActiveSteps(allSteps, nextCommitted);
      const nextStepIndex = nextCommitted.length;
      persistSnapshot(nextAnswers, nextStepIndex, stepsAfter);
      return nextCommitted;
    });
    setStepIndex((i) => i + 1);
  }, [
    answers,
    allSteps,
    committed,
    currentStep,
    editingCommitIndex,
    persistSnapshot,
    t,
    wheelLocale,
  ]);

  const undo = useCallback(() => {
    clearAssistantHold();
    setCommitted((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const nextStepIndex = prev.length - 1;

      setAnswers((a) => {
        let nextAnswers = { ...a };
        delete nextAnswers[last.fieldName];
        if (last.fieldName === 'constructionExperienceYears') {
          for (const key of WORKER_REGISTRY_PREVIOUS_JOB_FIELD_NAMES) {
            delete nextAnswers[key];
          }
        }
        const stepsAfter = workerRegistryActiveSteps(allSteps, prev.slice(0, -1));
        persistSnapshot(nextAnswers, nextStepIndex, stepsAfter);
        return nextAnswers;
      });
      setStepIndex(nextStepIndex);
      return prev.slice(0, -1);
    });
  }, [persistSnapshot, allSteps, clearAssistantHold]);

  /** Full thread shown (all steps answered): Edit on every bubble; Undo only on the last (matches general profile). */
  const showEditForCommitIndex = useCallback(
    (commitIndex: number) => {
      if (editingCommitIndex !== null) return false;
      if (!isComplete || committed.length === 0) return false;
      return commitIndex >= 0 && commitIndex < committed.length;
    },
    [editingCommitIndex, isComplete, committed.length]
  );

  const jumpToEditCommitIndex = useCallback(
    (commitIndex: number) => {
      if (allSteps.length === 0) return;
      const fullyComplete = stepIndex >= activeSteps.length;
      if (!fullyComplete || commitIndex < 0 || commitIndex >= committed.length) return;
      clearAssistantHold();
      setEditingCommitIndex(commitIndex);
    },
    [allSteps, activeSteps.length, stepIndex, committed.length, clearAssistantHold]
  );

  const showUndoForCommitIndex = useCallback(
    (commitIndex: number) => {
      if (editingCommitIndex !== null) return false;
      if (isComplete && committed.length > 0) {
        return commitIndex === committed.length - 1;
      }
      return stepIndex === commitIndex + 1;
    },
    [stepIndex, isComplete, committed.length, editingCommitIndex]
  );

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [committed, stepIndex, loading, isComplete, suppressNextAssistantBubble]);

  return {
    definition,
    loading,
    loadError,
    steps: activeSteps,
    stepIndex,
    currentStep,
    answers,
    committed,
    currentValue,
    setField,
    canSubmitCurrent,
    advance,
    undo,
    isComplete,
    showUndoForCommitIndex,
    showEditForCommitIndex,
    jumpToEditCommitIndex,
    threadEndRef,
    skipIntroDelay,
    editingCommitIndex,
    /** When false, hide the assistant bubble for the active step (brief beat after sending an answer). */
    assistantQuestionVisible: !suppressNextAssistantBubble,
  };
};
